import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';

import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { ControllerProps } from 'src/router';
import { ConfigurationService } from 'src/core/services/configuration.service';
import { DataLocalService } from 'src/core/services/data-local.service';
import { DialogService } from 'src/core/services/ui/dialog.service';

import { TaxonomyDataService } from 'src/app/general/taxonomies/form/taxonomy-form.data';
import { TaxonomyFormView } from 'src/app/general/taxonomies/form/taxonomy-form.view';
import { UserTaskDataService } from 'src/app/general/user-tasks/form/user-task.data';
import { UserTaskFormView } from 'src/app/general/user-tasks/form/user-task.view';

interface Props {
	modelType?: string;
	modelId?: any;
	visible: boolean;
	setBusy(isBusy: boolean);
	id?: any;
	onFinish: () => void;
	onEdit: () => void;
	daedline?: Date;
	assignedToId?: number;
	priority?: number;
	title?: string;
}

interface State {
	mode: ControlMode;
	defaultLanguage: string;
	working: boolean;
}

@observer
export class UserTaskFormController extends React.Component<Props, State> {
	@resolve
	private configurationService: ConfigurationService;

	@resolve
	private dialogService: DialogService;

	@resolve
	private toast: ToastNotifications;

	@observable
	dataService: UserTaskDataService;
	
	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			mode: 'display',
			defaultLanguage: '',
			working: false
		};
	}

	request = (id?: number) => {
		this.props.setBusy(true);

		this.loadConfiguration()
			.then(() => this.loadData(id))
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then(() => {
				this.props.setBusy(false);
			});
	}

	public componentWillReceiveProps(nextProps: Props) {
		if (nextProps.id != this.props.id)
			this.request(nextProps.id);
	}

	componentDidMount() {
		this.request(this.props.id);
	}

	private async loadConfiguration(): Promise<void> {
		const appConfig = await this.configurationService.current();

		this.setState({
			defaultLanguage: appConfig.defaultLanguage
		});
	}

	private async loadData(id?: number): Promise<void> {
		this.dataService = UserTaskDataService.make();
		const { defaultLanguage } = this.state;
		
		this.setState({ working: true });
		if (!id && this.props.modelId && this.props.modelType) {
			// Create new
			await this.dataService.add(this.props.modelType, this.props.modelId,this.props.daedline,this.props.assignedToId,this.props.priority,this.props.title);
			this.handleModeChange('edit');
		} else {
			// Load entity
			this.dataService.addFilter('id', 'eq', id);
			this.dataService.addSorting('id', 'asc', true);
			this.dataService.pageSize = 1;

			await this.dataService.load(query => query);
			this.handleModeChange('edit');
		}

		this.setState({ working: false });
	}

	@computed
	get title() {
		if (this.dataService && this.dataService.item) {
			return `User task '${this.dataService.item.title}'`;
		}

		return '';
	}

	handleModeChange = (mode: ControlMode): void => {
		this.setState({ mode });
	};

	handleSave = async (): Promise<void> => {

		this.setState({ working: true });
		const item = this.dataService.item;
		const errors = await item.validate(false);
		if (errors.length > 0) {
			this.toast.showToast('Validation error!', errors.map(e => e.text), 'error');
			return;
		}

		this.props.setBusy(true);
		this.props.onFinish();
		try {
			await this.dataService.save();
			this.toast.showDataSaved();
			this.props.onEdit();
		} catch (error) {
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		}

		this.setState({ working: false });
		this.props.setBusy(false);
	};

	handleCancel = async (): Promise<boolean> => {

		this.props.onFinish();

		return true;
	};

	render() {
		if (this.dataService == null || this.dataService.item == null) {
			return null;
		}

		const { mode, defaultLanguage } = this.state;
		const { setBusy } = this.props;

		return (
			<UserTaskFormView
				model={this.dataService.item}
				mode={mode}
				isNew={!this.props.id}
				onModeChange={this.handleModeChange}
				onSave={this.handleSave}
				onCancel={this.handleCancel}
				setBusy={setBusy}
				title={this.title}
				
				working={this.state.working}
				defaultLanguage={defaultLanguage}
				contextService={this.dataService}
				visible={this.props.visible}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/general/user-tasks/form/user-task.controller.tsx